import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { isEmpty, groupBy, sortBy, get } from 'lodash'
import cx from 'classnames'
import { lastChange } from 'helpers/moment'
import Input from 'components/Form/Input'
import Spinner from 'components/Spinner'

import MdSearch from 'react-icons/lib/md/search'

import classes from './List.scss'

import ListRow from './Row'
import ListColumn from './Column'

export class BetterList extends React.Component {
  constructor (props) {
    super(props)

    this.state = { filter: '' }
  }

  componentWillUnmount () {
    if (typeof this.props.onFilterCallback === 'function') this.props.onFilterCallback(this.props.collection)
    this.setState({ filter: '' })
  }

  getRowKey = (o, append = '') => {
    let identifierValue = get(o, this.props.rowUniqueIdentifier)

    return `${identifierValue}${append && `--${append}`}`
  };

  getGroups = () => {
    let collection = this.props.collection || []
    if (isEmpty(collection)) return false

    collection = this.filterCollection(collection)
    collection = this.sortCollection(collection)

    if (isEmpty(collection)) return false

    let groups = [collection] // wrap collection in an array to be a single group
    if (this.props.groupBy) groups = Object.values(groupBy(collection, this.props.groupBy))

    return this.sortGroup(groups)
  };

  sortGroup (groups) {
    if (!groups || !groups.length || !this.props.sortGroup) return groups

    if (typeof this.props.sortGroup === 'function') return groups.sort(this.props.sortGroup) // es6 sorting
    return sortBy(groups, `[0]${this.props.sortGroup}`)
  }

  sortCollection = collection => {
    if (isEmpty(collection) || !this.props.sort) return collection

    collection = collection.slice()
    if (!Array.isArray(this.props.sort)) return this.doSort(collection, this.props.sort) // just sort once

    let sort = this.props.sort.slice()
    sort.reverse().forEach(sort => {
      collection = this.doSort(collection, sort)
    })

    return collection
  };

  doSort = (collection, sort) => {
    if (isEmpty(collection) || !sort) return collection

    if (typeof sort === 'function') return collection.sort(sort) // es6 sorting
    return sortBy(collection, sort) // accepts array, string
  };

  filterCollection = (collection, filterValue = this.state.filter) => {
    if (isEmpty(collection) || !this.props.filter || !filterValue) return collection
    if (typeof filterValue.toLowerCase === 'function') filterValue = filterValue.toLowerCase()

    let filterFunction = typeof this.props.filter === 'function' && this.props.filter(filterValue)

    if (!filterFunction) {
      filterFunction = (model, index, _collection) => {
        return (
            (// is an array already
            (Array.isArray(this.props.filter) && this.props.filter || [this.props.filter])) // wrap in an array
          ).some(filterOn => {
            if (filterOn === '__options') { // SPECIAL Filter to compare against options (ie. printed or disabled)
              let rowOptions = this.props.getRowOptions && this.props.getRowOptions(model, index, _collection)

              if (rowOptions && typeof rowOptions.includes === 'function' && rowOptions.includes(filterValue)) {
                return true
              }
            }

            let compare = get(model, filterOn)
            if (compare && typeof compare.toLowerCase === 'function') {
              compare = compare.toLowerCase()

              if (compare && typeof compare.includes === 'function' && compare.includes(filterValue)) return true
            }
          })
      }
    }

    return collection.filter(filterFunction)
  };

  setFilter = (...args) => {
    let value = typeof args[0] === 'string' && args[0] || // directly passed a value
      args[0] && args[0].currentTarget && args[0].currentTarget.value || '' // is an onEvent(e)...

    if (value !== this.state.filter) { // skip forced rendering if the filtering is the same
      this.setState({ filter: value })

      // send the filtered collection off, if it differs..
      if (typeof this.props.onFilterCallback === 'function') {
        this.props.onFilterCallback(this.filterCollection(this.props.collection, value))
      }
    }
  };

  renderGroup = (collection, listIndex, groups) => {
    if (isEmpty(collection)) return null

    return [
      this.renderHeader(collection, listIndex, groups),
      this.renderBody(collection, listIndex, groups),
      typeof this.props.groupAfter === 'function' && this.props.groupAfter(collection, listIndex, groups) || null
    ]
  };

  renderGroupTitle = (collection, index, groups) => {
    if (!this.props.groupTitle && !this.props.groupDesc) return null

    let title = typeof this.props.groupTitle === 'function' && this.props.groupTitle(collection, index, groups) ||
        get(collection[0], this.props.groupTitle)
    let desc = typeof this.props.groupDesc === 'function' && this.props.groupDesc(collection, index, groups) ||
      get(collection[0], this.props.groupDesc)

    return <tr className={classes['group-title']} key={`group-title-${index}`}>
      <ListColumn {...this.props} colspan={100}>
        <span className={cx({
          [classes['column--row']]: true,
          [classes['column--group-title']]: true
        })}>{title}</span>

        {desc && <span className={cx({
          [classes['column--row']]: true,
          [classes['column--group-desc']]: true
        })}>{desc}</span>}
      </ListColumn>
    </tr>
  };

  renderHeader = (collection, index, groups) => {
    if (isEmpty(this.props.headers)) return null

    return <tbody>
      {this.renderGroupTitle(collection, index, groups)}
      <tr className={classes['header']} key={`group-header-${index}`}>
        {this.props.headers.map(this.renderHeaderColumn)}
      </tr>
    </tbody>
  };

  renderHeaderColumn = (name, index, headers) => { // index = this column
    // columnOptions[index] can be a string, array, or undefined
    let options = this.props.columnOptions && this.props.columnOptions[index] || []
    options = options.slice()
    options = Array.isArray(options) && options || [options]
    options.push('header')
    return <ListColumn {...this.props} key={`header-column-${index}`} options={options}>{name}</ListColumn>
  };

  getRenderedRows = collection => {
    if (!collection) return null
    let rows = []
    let stripedIndex = false

    if (this.props.stripeByField) {
      stripedIndex = [...new Set(collection.map(o => o[this.props.stripeByField]))] // generates a unique list
    }

    collection.forEach((o, i, a) => {
      const rowProps = typeof this.props.getRowProps === 'function' && { ...this.props.getRowProps(o, i, a) } || {}
      let options = this.props.getRowOptions && this.props.getRowOptions(o, i, a) || []

      if (this.props.stripeByField && stripedIndex && stripedIndex.length) {
        if (stripedIndex.indexOf(o[this.props.stripeByField]) % 2 === 1) options.push('striped')
      } else {
        if (i % 2 === 1) options.push('striped')
      }

      const props = {
        ...this.props,
        columns: this.props.getRowColumns(o, i, a),
        link: this.props.getRowLink && this.props.getRowLink(o, i, a),
        options,
        mapArguments: [o, i, a],
        ...rowProps
      }

      if (this.props.renderBeforeRow) {
        rows.push(
          <this.props.renderBeforeRow {...props} key={`row-before-${this.getRowKey(o, i, a)}` || `row-before:${i}`} />
        )
      }

      rows.push(<ListRow {...props} key={`row-${this.getRowKey(o, i, a)}` || `row:${i}`} />)

      if (this.props.renderAfterRow) {
        rows.push(
          <this.props.renderAfterRow {...props} key={`row-after-${this.getRowKey(o, i, a)}` || `row-after:${i}`} />
        )
      }
    })

    return rows
  };

  renderBody = (collection = []) => {
    return <tbody className={classes['items']}>
      {this.getRenderedRows(collection)}
    </tbody>
  };

  submitFilter = e => {
    e.preventDefault()

    document.activeElement && document.activeElement.blur()
    let inputs = document.querySelectorAll('input')
    for (let i = 0; i < inputs.length; i++) {
      inputs[i] && inputs[i].blur()
    }

    e.currentTarget && e.currentTarget.focus()
    e.currentTarget && e.currentTarget.blur()
    return true
  };

  renderFilter = () => {
    return <form className={classes['filter']} onSubmit={this.submitFilter}>
      <Input
        type='search'
        placeholder={this.props.filterPlaceholder || 'search list'} options={['large']}
        onChange={this.setFilter} value={this.state.filter}
        icon={<MdSearch />}
        iconSubmit
      />

      {this.props.quickFilters && <div className={classes['filter-note']}>
        Quick Search: {this.props.quickFilters.map((name, i) =>
          <mark className={classes.quickfilter} onClick={e => this.setFilter(name)} key={`quickfilter-${name}-${i}`}>
            {name}
          </mark>
        )}
      </div>}
    </form>
  };

  renderLastUpdated () {
    const lastUpdated = lastChange({ collection: this.props.pristineCollection, returnFuture: false })
    if (!this.props.notUpdatedMessage && !lastUpdated) return null

    return <div className={classes['last-updated']}>
      {!!lastUpdated && `Last updated ${lastUpdated}` || this.props.notUpdatedMessage}.
    </div>
  }

  renderNoResults = () => {
    return <h3 className={classes['list--empty']}>
      <p>{this.props.noResultsText}</p>
      {this.props.noResultsButton}
    </h3>
  };

  renderLoading = () => {
    return <div className={classes['list--loading']}>
      <Spinner />
    </div>
  };

  render () {
    let groups = this.getGroups()

    // defaults
    let results = this.props.loading && this.renderLoading() || this.renderNoResults()

    if (!isEmpty(groups)) {
      results = <table className={cx({
        [classes['list']]: true,
        [classes['list--partial']]: this.props.partial && classes['list--partial'],
        [classes['list--grouped']]: this.props.groupBy && classes['list--grouped']
      })}>
        {groups.map(this.renderGroup)}
      </table>
    }

    return <div>
      {this.props.filter && this.renderFilter()}
      {this.props.showLastUpdated && this.renderLastUpdated()}
      {this.props.children && <div className={classes.children}>{this.props.children}</div>}
      {results}
    </div>
  }
}

BetterList.propTypes = {
  pristineCollection: PropTypes.array, // Purely to get things before filtering out stuff like sold out and such..
  collection: PropTypes.array,
  columnOptions: PropTypes.array,
  headers: PropTypes.arrayOf(PropTypes.string),

  // to add additional <tr /> to the table, ie. notes or additional rows...
  renderBeforeRow: PropTypes.oneOfType([
    PropTypes.func, // render function
    PropTypes.node // node acting as a render function (component)
  ]),
  renderAfterRow: PropTypes.oneOfType([
    PropTypes.func, // render function
    PropTypes.node // node acting as a render function (component)
  ]),

  getRowLink: PropTypes.func,
  getRowOptions: PropTypes.func,
  getRowProps: PropTypes.func,
  getRowColumns: PropTypes.func.isRequired,
  rowUniqueIdentifier: PropTypes.string,

  stripeByField: PropTypes.oneOfType([
    PropTypes.string, // ie. customer_id
    PropTypes.bool // false only
  ]),

  groupBy: PropTypes.oneOfType([
    PropTypes.func, // passed: object
    PropTypes.string // ie. category.id
  ]),
  groupTitle: PropTypes.oneOfType([
    PropTypes.func, // passed: collection, index, groups
    PropTypes.string // ie. category.name
  ]),
  groupDesc: PropTypes.oneOfType([
    PropTypes.func, // passed: collection, index, groups
    PropTypes.string // ie. category.desc
  ]),
  groupAfter: PropTypes.func, // eg. a group_footer

  sort: PropTypes.oneOfType([
    PropTypes.func, // collection.sort(sort)
    PropTypes.string, // lodash.sortBy; can accept paths like 'customer.name'
    PropTypes.array, // array of string or function to be processed in reverse as strings/functions would
    PropTypes.bool // false only
  ]),
  sortGroup: PropTypes.oneOfType([
    PropTypes.func, // collection.sort(sort)
    PropTypes.string, // lodash.sortBy; can accept paths like 'category.priority'
    PropTypes.bool // false only
  ]),
  filter: PropTypes.oneOfType([  // lodash filter (string implementation) https://lodash.com/docs/4.17.2#filter
    PropTypes.func, // NOTE: this should return a filter function!! f(value) { return (object, index, array) ... }
    PropTypes.string, // to filter by
    PropTypes.arrayOf(PropTypes.string), // this is just multiple strings...
    PropTypes.bool // false only
  ]),
  filterPlaceholder: PropTypes.string,
  onFilterCallback: PropTypes.func, // get passed the filterFunction so you can locally update state?
  quickFilters: PropTypes.array,

  noResultsText: PropTypes.string,
  noResultsButton: PropTypes.node,

  showLastUpdated: PropTypes.bool,
  notUpdatedMessage: PropTypes.string,

  partial: PropTypes.bool,
  loading: PropTypes.bool,

  children: PropTypes.node // goes under filter/last updated at and above results.
}

BetterList.defaultProps = {
  collection: [],
  columnOptions: [],
  rowUniqueIdentifier: 'id',
  stripeByField: false,

  noResultsText: 'No Results Found',
  showLastUpdated: false,

  partial: false,
  loading: false
}

const mapStateToProps = (state, ownProps) => ({
  loading: ownProps.loading !== undefined && ownProps.loading || (state.api.fetching > 0)
})

export default connect(mapStateToProps)(BetterList)



// WEBPACK FOOTER //
// ./src/components/BetterList/List.js